import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { type Highlight } from '@/api/services/highlight'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

import { HIGHLIGHT_COLORS, type HighlightColor } from './highlight-colors'

type HighlightSheetProps = {
  open: boolean
  onClose: () => void
  selectedText?: string
  highlight?: Highlight | null
  onSave: (color: HighlightColor, note: string) => void
  onUpdate?: (id: number, color: HighlightColor, note: string) => void
  onDelete?: (id: number) => void
}

export const HighlightSheet = ({
  open,
  onClose,
  selectedText,
  highlight,
  onSave,
  onUpdate,
  onDelete,
}: HighlightSheetProps) => {
  const [selectedColor, setSelectedColor] = useState<HighlightColor>('yellow')
  const [note, setNote] = useState('')

  // 編集モードの場合、既存のデータをセット
  useEffect(() => {
    if (highlight) {
      setSelectedColor((highlight.color as HighlightColor) || 'yellow')
      setNote(highlight.note || '')
    } else {
      setSelectedColor('yellow')
      setNote('')
    }
  }, [highlight])

  const handleSave = () => {
    if (highlight && onUpdate) {
      // 更新モード
      onUpdate(highlight.id, selectedColor, note)
    } else {
      // 新規作成モード
      onSave(selectedColor, note)
    }
    handleClose()
  }

  const handleDelete = () => {
    if (highlight && onDelete) {
      onDelete(highlight.id)
      handleClose()
    }
  }

  const handleClose = () => {
    onClose()
    // リセット
    setNote('')
    setSelectedColor('yellow')
  }

  const isEditMode = !!highlight

  return (
    <BottomSheet
      open={open}
      onOpenChange={(open) => !open && handleClose()}
      title={isEditMode ? 'ハイライトを編集' : 'ハイライトを追加'}
    >
      <div className="p-4 space-y-4">
        {/* 選択されたテキスト表示 */}
        {selectedText && (
          <div className="bg-muted rounded-md p-3">
            <p className="text-sm text-muted-foreground mb-1">選択したテキスト:</p>
            <p className="text-sm text-foreground italic">"{selectedText}"</p>
          </div>
        )}

        {/* 元のテキスト表示（編集モード） */}
        {isEditMode && highlight.text && (
          <div className="bg-muted rounded-md p-3">
            <p className="text-sm text-muted-foreground mb-1">ハイライトされたテキスト:</p>
            <p className="text-sm text-foreground italic">"{highlight.text}"</p>
          </div>
        )}

        {/* 色選択 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">カラー</label>
          <div className="flex gap-3">
            {HIGHLIGHT_COLORS.map((colorOption) => (
              <button
                key={colorOption.value}
                onClick={() => setSelectedColor(colorOption.value)}
                className={`w-10 h-10 rounded-full transition-all ${colorOption.className} ${
                  selectedColor === colorOption.value
                    ? 'ring-2 ring-offset-2 ring-primary scale-110'
                    : 'hover:scale-105'
                }`}
                title={colorOption.label}
              />
            ))}
          </div>
        </div>

        {/* メモ入力 */}
        <div className="space-y-2">
          <label htmlFor="highlight-note" className="text-sm font-medium text-foreground">
            メモ（任意）
          </label>
          <Textarea
            id="highlight-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="メモを入力..."
            rows={4}
            className="resize-none"
          />
        </div>

        {/* ボタン */}
        <div className="flex gap-3">
          {isEditMode && onDelete && (
            <Button variant="destructive" onClick={handleDelete} size="icon" className="shrink-0">
              <Trash2 size={18} />
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} className="flex-1">
            キャンセル
          </Button>
          <Button onClick={handleSave} className="flex-1">
            {isEditMode ? '更新' : '保存'}
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
